import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const REACH_COMPARISON = [
  {
    feature: "Audience reach",
    paid: "Precise targeting by location, age, interests, and behaviour",
    organic: "Limited to 2-5% of your existing followers",
  },
  {
    feature: "Geographic targeting",
    paid: "Target specific postcodes, radius around office, or competitor areas",
    organic: "No control; relies on followers sharing content",
  },
  {
    feature: "Discovery potential",
    paid: "Guaranteed impressions to new audiences who have never heard of you",
    organic: "Algorithm-dependent; viral moments are rare and unpredictable",
  },
  {
    feature: "Speed of results",
    paid: "Immediate; ads begin delivering within hours of launch",
    organic: "Slow build; meaningful traction typically takes 3-6 months",
  },
  {
    feature: "Scalability",
    paid: "Increase budget to increase reach proportionally",
    organic: "Diminishing returns as follower growth plateaus",
  },
]

const COST_COMPARISON = [
  {
    feature: "Monthly budget",
    paid: "£100-500 for independent agents; £500-2,000 for multi-branch",
    organic: "Free in direct costs, but significant time investment",
  },
  {
    feature: "Cost per thousand impressions (CPM)",
    paid: "£3-7 on Facebook/Instagram for property content",
    organic: "£0 per impression, but opportunity cost of 5-10 hrs/week",
  },
  {
    feature: "Cost per lead",
    paid: "£5-25 per valuation lead with optimised campaigns",
    organic: "Difficult to measure; typically higher when time is factored in",
  },
  {
    feature: "Return on investment",
    paid: "Measurable and attributable; track cost per instruction won",
    organic: "Hard to attribute; builds brand equity over time",
  },
  {
    feature: "Risk profile",
    paid: "Budget lost if targeting or creative is poor",
    organic: "Time lost if content does not resonate",
  },
]

const TIME_COMPARISON = [
  {
    feature: "Weekly time commitment",
    paid: "2-3 hours (setup, monitoring, optimisation)",
    organic: "5-10 hours (content creation, posting, engagement)",
  },
  {
    feature: "Content volume required",
    paid: "2-4 high-quality ad creatives per month",
    organic: "3-5 posts per week minimum to maintain visibility",
  },
  {
    feature: "Skill set needed",
    paid: "Ad platform knowledge, basic analytics, budget management",
    organic: "Content creation, copywriting, community management, consistency",
  },
  {
    feature: "Time to first results",
    paid: "Days; first leads can arrive within 48 hours",
    organic: "Months; consistent posting for 90+ days before meaningful traction",
  },
  {
    feature: "Maintenance effort",
    paid: "Weekly check-ins to optimise targeting and refresh creatives",
    organic: "Daily engagement, replies, stories, and community building",
  },
]

const BUDGET_GUIDE = [
  {
    size: "Solo agent / small independent",
    branches: "1 branch",
    organic: "60% organic, 40% paid",
    monthlyBudget: "£100-250/month",
    focus: "Boost best listings, run monthly valuation campaigns",
  },
  {
    size: "Growing independent",
    branches: "2-3 branches",
    organic: "50% organic, 50% paid",
    monthlyBudget: "£250-500/month",
    focus: "Always-on brand awareness plus listing-specific boosts",
  },
  {
    size: "Multi-branch agency",
    branches: "4-10 branches",
    organic: "40% organic, 60% paid",
    monthlyBudget: "£500-1,500/month",
    focus: "Dedicated campaigns per branch, retargeting, valuation funnels",
  },
  {
    size: "Regional corporate",
    branches: "10+ branches",
    organic: "30% organic, 70% paid",
    monthlyBudget: "£1,500-5,000/month",
    focus: "Full-funnel strategy with brand, consideration, and conversion campaigns",
  },
]

const FAQ = [
  {
    q: "Should estate agents pay for social media advertising?",
    a: "Yes, if you want predictable, measurable results. Organic reach on platforms like Facebook and Instagram has declined to 2-5% of followers for business pages. Paid advertising guarantees your content reaches the right audience, in the right area, at the right time. Even a modest budget of £100-250 per month can generate meaningful valuation leads.",
  },
  {
    q: "How much should an estate agent spend on social media ads?",
    a: "A reasonable starting budget is £100-500 per month for an independent agent. Multi-branch agencies typically invest £500-2,000 per month. The key is to start small, measure results, and scale what works. Focus spending on your highest-value activities: promoting premium listings, running valuation campaigns, and retargeting website visitors.",
  },
  {
    q: "Is organic social media still worth it for estate agents?",
    a: "Absolutely. Organic content builds trust, demonstrates local expertise, and keeps your brand visible to people who already follow you. It complements paid activity by giving potential clients a reason to trust you after they first encounter your ads. The most effective agencies do both, not one or the other.",
  },
  {
    q: "What type of content works best for paid property ads?",
    a: "Video consistently outperforms static images in property advertising. Short-form video (15-30 seconds) showcasing a property walkthrough or lifestyle shots generates 2-3x the engagement of photo carousels. The same video you create for organic posting can be repurposed as a paid ad, making video production doubly valuable.",
  },
  {
    q: "How quickly can estate agents see results from paid social ads?",
    a: "Paid campaigns typically deliver first impressions within hours of launch. For listing promotion, you can expect enquiries within 24-72 hours. Valuation campaigns usually need 2-4 weeks to generate consistent leads as the platform optimises delivery. Most agents see a clear pattern of what works within their first 60 days.",
  },
  {
    q: "Can I run paid ads and organic content with the same videos?",
    a: "Yes, and this is the most efficient approach. A well-produced property video works as an organic post on your feed, a paid ad targeting local buyers, a story or reel for engagement, and a website embed. Tools like QuiteRight make it easy to create one video that serves every channel.",
  },
  {
    q: "What is the best platform for estate agent paid ads?",
    a: "Facebook and Instagram (run through Meta Ads Manager) deliver the best results for most estate agents. Facebook has the strongest homeowner demographic in the UK, while Instagram Reels excels at showcasing property visually. TikTok is emerging but currently better suited to organic brand-building than direct response advertising.",
  },
  {
    q: "How do I measure the ROI of social media marketing for my agency?",
    a: "For paid campaigns, track cost per lead, cost per valuation booked, and cost per instruction won. Most agents find a cost per valuation lead of £5-25 through paid social. For organic, measure engagement rate, follower growth, website traffic from social, and (qualitatively) how often prospects mention your social presence during appraisals.",
  },
]

/* ------------------------------------------------------------------ */
/*  Reusable badge                                                     */
/* ------------------------------------------------------------------ */

function Badge({ variant, children }: { variant: "paid" | "organic"; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        variant === "paid"
          ? "bg-red/10 text-red"
          : "bg-charcoal/10 text-charcoal"
      }`}
    >
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/*  Comparison table component                                         */
/* ------------------------------------------------------------------ */

function ComparisonTable({
  title,
  subtitle,
  rows,
}: {
  title: string
  subtitle: string
  rows: { feature: string; paid: string; organic: string }[]
}) {
  return (
    <section className="border-t border-stone px-6 py-16">
      <div className="mx-auto max-w-4xl">
        <h2 className="font-display text-2xl font-bold text-charcoal">
          {title}
          <span className="text-red">.</span>
        </h2>
        <p className="mt-2 text-sm text-slate">{subtitle}</p>

        <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone bg-cream/50">
                <th className="px-4 py-3 text-left font-medium text-charcoal">
                  Factor
                </th>
                <th className="px-4 py-3 text-left font-medium text-charcoal">
                  <Badge variant="paid">Paid</Badge>
                </th>
                <th className="px-4 py-3 text-left font-medium text-charcoal">
                  <Badge variant="organic">Organic</Badge>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone">
              {rows.map((row) => (
                <tr key={row.feature} className="hover:bg-cream/30">
                  <td className="px-4 py-3 font-medium text-charcoal">
                    {row.feature}
                  </td>
                  <td className="px-4 py-3 text-slate">{row.paid}</td>
                  <td className="px-4 py-3 text-slate">{row.organic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  Check icon                                                         */
/* ------------------------------------------------------------------ */

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
    >
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  Arrow icon                                                         */
/* ------------------------------------------------------------------ */

function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="size-3.5 transition-transform group-hover:translate-x-0.5"
    >
      <path
        fillRule="evenodd"
        d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function PaidVsOrganicSocial({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Paid Video Ads vs Organic Social for Estate Agents",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Paid Video Ads vs Organic Social for Estate Agents" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Paid video ads vs organic social for estate agents
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Should your agency commit ad spend, or is organic social enough?
            A practical, data-backed comparison to help you decide where
            your marketing budget (and time) delivers the strongest return.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Badge variant="paid">Paid</Badge>
            <span className="text-xs text-slate">vs</span>
            <Badge variant="organic">Organic</Badge>
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "2-5%", label: "of followers see organic posts from business pages" },
            { stat: "£3-7", label: "CPM for targeted property ads on Meta platforms" },
            { stat: "5-10 hrs", label: "per week to maintain a consistent organic presence" },
            { stat: "3x", label: "higher ROI when agencies combine paid and organic" },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Intro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The organic reach problem
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Five years ago, posting a property on your agency's <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">Facebook</a> page
              reached a meaningful portion of your audience. Today, organic reach
              for business pages on Facebook sits at roughly 2-5% of followers.
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">Instagram</a> is slightly better, but the trajectory is the same:
              platforms want businesses to pay for visibility.
            </p>
            <p>
              This does not mean organic social is worthless. Far from it. But
              it does mean that relying solely on organic content to generate
              leads, win instructions, and sell properties is increasingly
              difficult. The agencies seeing the strongest results in 2026
              are those that combine a solid organic foundation with targeted
              paid campaigns. Our guide to{" "}
              <Link href="/guides/social-media-estate-agents" className="text-red underline decoration-red/30 hover:text-charcoal">social media for estate agents</Link>{" "}
              covers the fundamentals.
            </p>
          </div>
        </div>
      </section>

      {/* Deep-dive cards: Paid vs Organic */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Paid vs organic: strengths and limitations
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            Neither approach is inherently better. Each excels in different areas.
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {/* Paid card */}
            <div className="rounded-xl border border-red/20 bg-white p-6">
              <div className="flex items-center gap-2">
                <Badge variant="paid">Paid</Badge>
                <span className="text-sm font-medium text-charcoal">Video Advertising</span>
              </div>

              <h3 className="mt-4 font-display text-base font-bold text-charcoal">
                Strengths
              </h3>
              <ul className="mt-2 space-y-2 text-sm text-slate">
                {[
                  "Precise targeting: reach buyers by location, demographics, and behaviour",
                  "Immediate results: first enquiries within 24-72 hours of launch",
                  "Measurable ROI: track every pound from impression to instruction",
                  "Scalable: increase budget to increase reach proportionally",
                  "Retargeting: re-engage people who visited your website or watched your videos",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <CheckIcon className="mt-0.5 size-4 shrink-0 text-red" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="mt-5 font-display text-base font-bold text-charcoal">
                Limitations
              </h3>
              <ul className="mt-2 space-y-2 text-sm text-slate">
                {[
                  "Requires ongoing budget; results stop when spending stops",
                  "Learning curve with ad platforms (Meta Ads Manager, TikTok Ads, etc.)",
                  "Poor creative or targeting wastes money quickly",
                  "Can feel transactional without supporting organic content",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-slate/40" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Organic card */}
            <div className="rounded-xl border border-stone bg-white p-6">
              <div className="flex items-center gap-2">
                <Badge variant="organic">Organic</Badge>
                <span className="text-sm font-medium text-charcoal">Social Content</span>
              </div>

              <h3 className="mt-4 font-display text-base font-bold text-charcoal">
                Strengths
              </h3>
              <ul className="mt-2 space-y-2 text-sm text-slate">
                {[
                  "Builds trust and credibility over time with consistent content",
                  "Free in direct costs (no ad spend required)",
                  "Demonstrates local expertise through market updates and area knowledge",
                  "Creates a content library that supports your brand for years",
                  "Humanises your agency through behind-the-scenes and team content",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <CheckIcon className="mt-0.5 size-4 shrink-0 text-charcoal" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="mt-5 font-display text-base font-bold text-charcoal">
                Limitations
              </h3>
              <ul className="mt-2 space-y-2 text-sm text-slate">
                {[
                  "Declining reach: only 2-5% of followers see business page posts",
                  "Time-intensive: 5-10 hours per week for meaningful consistency",
                  "Slow results; 3-6 months before building real traction",
                  "Difficult to attribute leads or instructions directly to organic effort",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-slate/40" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table: Reach & Visibility */}
      <ComparisonTable
        title="Reach and visibility"
        subtitle="How each approach delivers your content to prospective buyers and sellers across key platforms."
        rows={REACH_COMPARISON}
      />

      {/* Comparison table: Cost & ROI */}
      <ComparisonTable
        title="Cost and ROI"
        subtitle="The real cost of each approach when you factor in time, money, and measurability."
        rows={COST_COMPARISON}
      />

      {/* Comparison table: Time & Effort */}
      <ComparisonTable
        title="Time and effort"
        subtitle="What each approach demands from you and your team on a weekly basis."
        rows={TIME_COMPARISON}
      />

      {/* Hybrid approach */}
      <section className="border-t border-stone bg-cream/40 px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal md:text-3xl">
              The hybrid approach: the strategy that actually works
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-6 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The most successful estate agencies in 2026 are not choosing
              between paid and organic. They are doing both, strategically.
              The recommended split for most agencies is roughly 70% organic
              foundation with 30% paid amplification. For a deeper look at
              which platforms to prioritise, see our{" "}
              <Link href="/guides/instagram-vs-facebook-vs-tiktok-property-ads" className="text-red underline decoration-red/30 hover:text-charcoal">Instagram vs Facebook vs TikTok</Link>{" "}
              comparison.
            </p>
            <p>
              Here is what that looks like in practice. Your organic content
              on platforms like{" "}
              <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">TikTok</a>,{" "}
              <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">YouTube</a>, and Instagram
              establishes your agency as the local expert. Market updates,
              sold stories, area guides, team introductions, and property
              showcases keep your feed active and give potential clients a
              reason to trust you. This is your baseline.
            </p>
            <p>
              Your paid budget then amplifies the content that matters most.
              Boost your best-performing listings to reach buyers beyond your
              followers. Run targeted valuation campaigns through{" "}
              <a href="https://www.facebook.com/business/ads" target="_blank" rel="noopener noreferrer" className="text-red underline decoration-red/30 hover:text-charcoal">Meta Ads Manager</a>{" "}
              to generate instruction leads.{" "}
              <Link href="/guides/retargeting-vs-cold-audience-property-video-ads" className="text-red underline decoration-red/30 hover:text-charcoal">Retarget people who visited your website</Link>{" "}
              but did not make contact. Every pound of ad spend works harder when
              it is backed by a credible organic presence.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              {
                pct: "70%",
                label: "Organic foundation",
                desc: "Consistent posting, community engagement, brand building, and local expertise content.",
              },
              {
                pct: "20%",
                label: "Paid: listing promotion",
                desc: "Boost your best listings to targeted local audiences beyond your existing followers.",
              },
              {
                pct: "10%",
                label: "Paid: lead generation",
                desc: "Valuation campaigns, retargeting, and instruction-focused ads with clear calls to action.",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-stone bg-white p-5"
              >
                <p className="font-display text-2xl font-bold text-red">
                  {item.pct}
                </p>
                <p className="mt-1 text-sm font-semibold text-charcoal">
                  {item.label}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-slate">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Budget guide */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Budget guide by agency size
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            What to spend at each stage of growth, and where to focus your investment. For platform-specific budgeting, see our{" "}
            <Link href="/guides/cost-per-lead-video-ads-vs-traditional-property-marketing" className="text-red underline decoration-red/30 hover:text-charcoal">cost per lead guide</Link>.
          </p>

          <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-cream/50">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Agency size
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Recommended split
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Monthly ad budget
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Focus areas
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {BUDGET_GUIDE.map((row) => (
                  <tr key={row.size} className="hover:bg-cream/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-charcoal">{row.size}</p>
                      <p className="text-xs text-slate">{row.branches}</p>
                    </td>
                    <td className="px-4 py-3 text-slate">{row.organic}</td>
                    <td className="px-4 py-3 font-medium text-charcoal">
                      {row.monthlyBudget}
                    </td>
                    <td className="px-4 py-3 text-slate">{row.focus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* QuiteRight callout */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              One video, every channel
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold text-cream md:text-4xl">
              The same video works for paid and organic
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-warm-grey">
            This is one of the biggest advantages of investing in{" "}
            <Link href="/guides/property-video-marketing" className="text-red underline decoration-red/30 hover:text-cream">property video marketing</Link>.
            A property video created with{" "}
            <Link
              href="/features"
              className="text-red underline decoration-red/30 transition-colors hover:text-cream"
            >
              QuiteRight
            </Link>{" "}
            works as an organic post on your feed, a paid ad targeting local
            buyers, a story or reel for engagement, an embed on your website,
            and a link in your email campaigns. One asset, five channels, zero
            duplication of effort. For a platform-specific breakdown, read our{" "}
            <Link href="/guides/youtube-ads-vs-facebook-ads-estate-agents" className="text-red underline decoration-red/30 hover:text-cream">YouTube ads vs Facebook ads</Link>{" "}
            comparison.
          </p>
          <div className="mt-8 grid gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-3">
            {[
              { label: "Organic feed post", desc: "Share to followers, build engagement" },
              { label: "Paid ad creative", desc: "Target buyers by postcode and interest" },
              { label: "Website and portals", desc: "Embed on listings, send via email" },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-charcoal px-5 py-5 text-center"
              >
                <p className="text-sm font-semibold text-cream">
                  {item.label}
                </p>
                <p className="mt-1 text-xs text-warm-grey">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related guides<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/social-media-estate-agents",
                title: "Social media for estate agents",
                desc: "The complete guide to building a social presence that wins instructions.",
              },
              {
                href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads",
                title: "Instagram vs Facebook vs TikTok for property ads",
                desc: "Which platform delivers the best results for your agency.",
              },
              {
                href: "/guides/instagram-reels-estate-agents",
                title: "Instagram Reels for estate agents",
                desc: "How to create short-form video content that drives enquiries.",
              },
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing",
                desc: "Why video is the most effective marketing tool in property today.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <ArrowIcon />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            One video. Every channel. Maximum return
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Whether you are investing in paid ads, building your organic
            presence, or (ideally) doing both, QuiteRight gives you the
            video content that makes every post and every pound work harder.
            See our full{" "}
            <Link href="/features" className="text-red underline decoration-red/30 hover:text-cream">feature set</Link>{" "}
            or{" "}
            <Link href="/sign-up" className="text-red underline decoration-red/30 hover:text-cream">get started free</Link>.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
